import React, { useEffect, useState } from 'react';
import { Button, Drawer, Spin, Typography, Empty } from 'antd';
import legalService from '../../services/legal.service';
import LegalDocumentBody from './LegalDocumentBody';
import { getLegalDocumentFallbackContent } from '../../constants/legalDocumentFullContent';

type LegalDocumentDrawerProps = {
  slug: string | null;
  open: boolean;
  onClose: () => void;
};

const LegalDocumentDrawer: React.FC<LegalDocumentDrawerProps> = ({ slug, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [version, setVersion] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !slug) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const doc = await legalService.getDocument(slug);
        if (cancelled) return;
        setTitle(doc.title);
        setVersion(doc.version);
        setSummary(doc.summary || '');
        setContent(doc.content || null);
      } catch {
        if (!cancelled) {
          const fallback = getLegalDocumentFallbackContent(slug);
          const firstLine = fallback?.split('\n').find((line) => line.trim());
          setTitle(firstLine || '');
          setVersion(fallback ? '1.0' : '');
          setSummary('');
          setContent(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, slug]);

  const hasBody = slug && !!(content?.trim() || getLegalDocumentFallbackContent(slug));

  return (
    <Drawer
      title={
        title
          ? title.startsWith('《')
            ? title
            : `《${title}》`
          : '法律文档'
      }
      open={open}
      onClose={onClose}
      size={720}
      destroyOnHidden
      zIndex={2000}
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            已阅读
          </Button>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !hasBody ? (
        <Empty description="文档不存在或暂时无法加载" />
      ) : (
        <>
          {version ? (
            <Typography.Text type="secondary">版本 {version}</Typography.Text>
          ) : null}
          {summary ? (
            <Typography.Paragraph style={{ marginTop: 16 }}>{summary}</Typography.Paragraph>
          ) : null}
          <div style={{ marginTop: 16 }}>
            {slug ? <LegalDocumentBody slug={slug} content={content} /> : null}
          </div>
        </>
      )}
    </Drawer>
  );
};

export default LegalDocumentDrawer;
